import _get from "lodash/get";
import { getInstitution, getProduct } from './common/general-utils';
import { getNameWithFallbacks } from '../utils';
/**
 * Get the name to display for an offer.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     The offer name.
 */

export var getName = function getName(marketplaceEntity) {
  return getNameWithFallbacks(marketplaceEntity);
};
/**
 * Gets the name of the entity as the alt text for the associated image.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity.
 *
 * @return     {string}                     The Alt text.
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return getName(marketplaceEntity);
};
/**
 * Gets source URL of the image associated with the offer.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity.
 *
 * @return     {string}                     The URL of the image.
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'details.logo_url.value');
};
/**
 * Get the issuer.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     Issuer name.
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'name');
};
/**
 * Gets the "cons" bullets
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {Array}  The "cons" array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.cons_bullets.value');
};
/**
 * Get the coverage options.
 *
 * @param      {Object} marketplaceEntity   The insurance entity
 *
 * @returns    {string}                     The coverage options
 */

export var getCoverageOptions = function getCoverageOptions(marketplaceEntity) {
  var coverageOption = _get(getProduct(marketplaceEntity), 'details.coverage_options.value'); // TODO: Cleanup this stop-gap fix after the long-term fix (REVS-229/PP-5364) has been shipped


  if (coverageOption === 'Above average') {
    return 'About average';
  }

  return coverageOption;
};
/**
 * Gets the discounts.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity.
 *
 * @return     {string}                     The discounts.
 */

export var getDiscounts = function getDiscounts(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.discounts.value');
};
/**
 * Gets the ease of use.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity.
 *
 * @return     {string}                     The ease of use.
 */

export var getEaseOfUse = function getEaseOfUse(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.ease_of_use.value');
};
/**
 * Gets the financial strength rating.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     The financial strength rating.
 */

export var getFinancialStrengthRating = function getFinancialStrengthRating(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.financial_strength_rating.value');
};
/**
 * Gets the financial strength rating annotation.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     The financial strength rating annotation.
 */

export var getFinancialStrengthRatingAnnotation = function getFinancialStrengthRatingAnnotation(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.financial_strength_rating_annotation.value');
};
/**
 * Get URL of the review.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     The URL of the review.
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.nw_review_url.value');
};
/**
 * Gets the amount of NAIC complaints.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     The amount of NAIC complaints.
 */

export var getNAICComplaints = function getNAICComplaints(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.naic_complaints.value');
};
/**
 * Gets the policies offered.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity
 *
 * @return     {string}                     The string description of the policies offered.
 */

export var getPoliciesOffered = function getPoliciesOffered(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.policies_offered.value');
};
/**
 * Gets the policies_offered_annotation value for the entity.
 * @param {Object} marketplaceEntity The insurance entity.
 * @returns {string}  The annotation for the policies_offered field.
 */

export var getPoliciesOfferedAnnotation = function getPoliciesOfferedAnnotation(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.policies_offered_annotation.value');
};
/**
 * Gets the "pros" bullets
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {Array}  The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.pros_bullets.value');
};
/**
 * Gets nw_rating value.
 *
 * @param      {Object}  marketplaceEntity  The insurance entity.
 *
 * @return     {number}                     The star rating.
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.nw_rating.value');
};
/**
 * Gets the star rating tooltip.
 *
 * @return     {string}                     The star rating tooltip.
 */

export var getStarRatingTooltip = function getStarRatingTooltip() {
  return "NerdWallet's ratings are determined by our editorial team. The scoring formula takes into account consumer complaint and customer satisfaction data.";
};
/**
 * Gets the category for the entity.
 * @param {Object} marketplaceEntity The insurance entity.
 * @returns {string} The category.
 */

export var getCategory = function getCategory(marketplaceEntity) {
  var _getProduct, _getProduct$details, _getProduct$details$c;

  return (_getProduct = getProduct(marketplaceEntity)) === null || _getProduct === void 0 ? void 0 : (_getProduct$details = _getProduct.details) === null || _getProduct$details === void 0 ? void 0 : (_getProduct$details$c = _getProduct$details.category) === null || _getProduct$details$c === void 0 ? void 0 : _getProduct$details$c.value;
};
/**
 * Gets the can_buy_online value for the entity.
 * @param {Object} marketplaceEntity The insurance entity.
 * @returns {boolean} Whether the insurance can be bought online.
 */

export var getCanBuyOnline = function getCanBuyOnline(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.can_buy_online.value');
};
/**
 * Gets the can_buy_online_annotation value for the entity.
 * @param {Object} marketplaceEntity The insurance entity.
 * @returns {string} The annotation for the can_buy_online field.
 */

export var getCanBuyOnlineAnnotation = function getCanBuyOnlineAnnotation(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.can_buy_online_annotation.value');
};
/**
 * Gets the sub product type.
 *
 * @param     {Object}  marketplaceEntity  The insurance entity.
 *
 * @return    {string}                     The sub product type.
 */

export var getSubProductType = function getSubProductType(marketplaceEntity) {
  var _product$details, _product$details$sub_, _product$details2, _product$details2$cat;

  var product = getProduct(marketplaceEntity);
  /*
   * According to the following link the category is now legacy so we should prioritize the sub product type.
   * @link {https://github.com/NerdWallet/marketplace/blob/f3644e98bbddd180b13d3de97116cb3cd6de07a2/src/schemas/insurance/product_details.json#L3}
   * However, to ensure that we have a value fallback to category because not all offers have sub product type set.
   */

  return (product === null || product === void 0 ? void 0 : (_product$details = product.details) === null || _product$details === void 0 ? void 0 : (_product$details$sub_ = _product$details.sub_product_type) === null || _product$details$sub_ === void 0 ? void 0 : _product$details$sub_.value) || (product === null || product === void 0 ? void 0 : (_product$details2 = product.details) === null || _product$details2 === void 0 ? void 0 : (_product$details2$cat = _product$details2.category) === null || _product$details2$cat === void 0 ? void 0 : _product$details2$cat.value);
};
/**
 * Gets the tag≈line text.
 *
 * @param   {Object} marketplaceEntity  The insurance entity.
 *
 * @return  {string}                    Tagline text.
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.tagline.value');
};